<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import ResolutionReportsAPI from 'dashboard/api/resolutionReports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');

const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 29);

const fromDate = ref(defaultFrom.toISOString().slice(0, 10));
const toDate = ref(today.toISOString().slice(0, 10));
const selectedAgentId = ref('');
const selectedInboxId = ref('');
const searchQuery = ref('');
const page = ref(1);

const loading = ref(false);
const records = ref([]);
const totalCount = ref(0);
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / 25)));
const isDownloading = ref(false);

const fromTimestamp = computed(() => Math.floor(new Date(fromDate.value).getTime() / 1000));
const toTimestamp = computed(() => Math.floor(new Date(toDate.value + 'T23:59:59').getTime() / 1000));

const queryParams = computed(() => ({
  from: fromTimestamp.value,
  to: toTimestamp.value,
  user_ids: selectedAgentId.value || undefined,
  inbox_id: selectedInboxId.value || undefined,
  q: searchQuery.value || undefined,
  page: page.value,
}));

async function fetchRecords() {
  loading.value = true;
  try {
    const { data } = await ResolutionReportsAPI.get(queryParams.value);
    records.value = data.data || [];
    totalCount.value = data.meta?.total_count || 0;
  } catch {
    records.value = [];
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
}

async function handleDownload() {
  isDownloading.value = true;
  try {
    const { data } = await ResolutionReportsAPI.download({
      from: fromTimestamp.value,
      to: toTimestamp.value,
      user_ids: selectedAgentId.value || undefined,
      inbox_id: selectedInboxId.value || undefined,
      q: searchQuery.value || undefined,
    });
    downloadCsvFile('resolutions.csv', data);
  } catch {
    // ignore
  } finally {
    isDownloading.value = false;
  }
}

function openConversation(displayId) {
  const accountId = window.location.pathname.split('/')[3];
  window.open(
    `${window.location.origin}/app/accounts/${accountId}/conversations/${displayId}`,
    '_blank'
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CATEGORY_COLORS = {
  'Problema resolvido': 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 border-blue-200',
  'Cliente satisfeito / Venda realizada': 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 border-emerald-200',
  'Sem resposta do cliente': 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 border-amber-200',
  'Encerrado pelo sistema': 'bg-n-alpha-2 text-n-slate-10 border-n-weak',
  'Contato indevido / Spam': 'bg-red-50 dark:bg-red-900/20 text-red-600 border-red-200',
  'Outros': 'bg-n-alpha-2 text-n-slate-10 border-n-weak',
};

function reasonBadgeClass(reason) {
  if (!reason) return 'bg-n-alpha-2 text-n-slate-9 border-n-weak';
  const category = Object.keys(CATEGORY_COLORS).find(k => reason.startsWith(k));
  return category ? CATEGORY_COLORS[category] : 'bg-n-alpha-2 text-n-slate-10 border-n-weak';
}

function reasonCategory(reason) {
  if (!reason) return null;
  return Object.keys(CATEGORY_COLORS).find(k => reason.startsWith(k)) || null;
}

function reasonDetail(reason) {
  if (!reason) return null;
  const sep = ' — ';
  const idx = reason.indexOf(sep);
  return idx !== -1 ? reason.slice(idx + sep.length) : null;
}

const topAgent = computed(() => {
  const agentCounts = {};
  records.value.forEach(r => {
    if (r.resolved_by) {
      const id = r.resolved_by.id;
      agentCounts[id] = agentCounts[id] || { name: r.resolved_by.name, count: 0 };
      agentCounts[id].count += 1;
    }
  });
  const sorted = Object.values(agentCounts).sort((a, b) => b.count - a.count);
  return sorted[0] || null;
});

function resetFilters() {
  selectedAgentId.value = '';
  selectedInboxId.value = '';
  searchQuery.value = '';
  page.value = 1;
}

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  fetchRecords();
});

watch([fromDate, toDate, selectedAgentId, selectedInboxId, searchQuery], () => {
  page.value = 1;
  fetchRecords();
});
watch(page, fetchRecords);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">
          {{ t('RESOLUTION_REPORTS.HEADER') }}
        </h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          {{ t('RESOLUTION_REPORTS.METRICS.TOTAL_TOOLTIP') }}
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
        :disabled="isDownloading || records.length === 0"
        @click="handleDownload"
      >
        <span class="i-lucide-download w-4 h-4" :class="{ 'animate-bounce': isDownloading }" />
        {{ t('RESOLUTION_REPORTS.DOWNLOAD_CSV') }}
      </button>
    </div>

    <!-- Filters -->
    <div class="flex gap-3 flex-wrap items-center">
      <!-- Date range -->
      <div class="flex items-center gap-2 bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2">
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-10" />
        <input v-model="fromDate" type="date" class="text-sm bg-transparent text-n-slate-12 outline-none" />
        <span class="text-n-slate-9">–</span>
        <input v-model="toDate" type="date" class="text-sm bg-transparent text-n-slate-12 outline-none" />
      </div>

      <!-- Agent filter -->
      <select
        v-model="selectedAgentId"
        class="text-sm bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-11 outline-none"
      >
        <option value="">{{ t('RESOLUTION_REPORTS.FILTERS.AGENTS_LABEL') }}</option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>

      <!-- Inbox filter -->
      <select
        v-model="selectedInboxId"
        class="text-sm bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-11 outline-none"
      >
        <option value="">{{ t('RESOLUTION_REPORTS.FILTERS.INBOX_LABEL') }}</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>

      <!-- Search -->
      <div class="flex items-center gap-2 bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 flex-1 min-w-[180px]">
        <span class="i-lucide-search w-4 h-4 text-n-slate-10 shrink-0" />
        <input
          v-model="searchQuery"
          type="text"
          class="text-sm bg-transparent text-n-slate-12 outline-none w-full"
          :placeholder="t('RESOLUTION_REPORTS.FILTERS.SEARCH_PLACEHOLDER')"
        />
        <button v-if="searchQuery" class="text-n-slate-9 hover:text-n-slate-12" @click="searchQuery = ''">
          <span class="i-lucide-x w-3.5 h-3.5" />
        </button>
      </div>

      <!-- Reset -->
      <button
        v-if="selectedAgentId || selectedInboxId || searchQuery"
        class="text-xs text-n-brand underline"
        @click="resetFilters"
      >
        Limpar filtros
      </button>
    </div>

    <!-- Metrics cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <div class="flex items-center justify-between">
          <p class="text-xs font-medium text-n-slate-10">{{ t('RESOLUTION_REPORTS.METRICS.TOTAL_LABEL') }}</p>
          <span class="i-lucide-check-circle-2 w-4 h-4 text-emerald-500" />
        </div>
        <p class="text-2xl font-bold text-n-slate-12">{{ totalCount }}</p>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <div class="flex items-center justify-between">
          <p class="text-xs font-medium text-n-slate-10">{{ t('RESOLUTION_REPORTS.METRICS.TOP_AGENT_LABEL') }}</p>
          <span class="i-lucide-trophy w-4 h-4 text-amber-500" />
        </div>
        <p class="text-lg font-bold text-n-slate-12 truncate">
          {{ topAgent ? `${topAgent.name} (${topAgent.count})` : '—' }}
        </p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16 text-n-slate-9">
      <span class="i-lucide-loader-2 w-5 h-5 animate-spin mr-2" />
      {{ t('RESOLUTION_REPORTS.LOADING') }}
    </div>

    <!-- Empty -->
    <div
      v-else-if="records.length === 0"
      class="flex flex-col items-center justify-center py-16 gap-3 text-n-slate-9"
    >
      <span class="i-lucide-clipboard-list w-10 h-10 opacity-30" />
      <p class="text-sm">{{ t('RESOLUTION_REPORTS.EMPTY_STATE') }}</p>
    </div>

    <!-- Table -->
    <div v-else class="w-full overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm text-n-slate-11">
        <thead class="bg-n-alpha-2 text-xs text-n-slate-10 font-medium">
          <tr>
            <th class="px-4 py-3 text-left whitespace-nowrap">{{ t('RESOLUTION_REPORTS.TABLE.DATE') }}</th>
            <th class="px-4 py-3 text-left whitespace-nowrap">{{ t('RESOLUTION_REPORTS.TABLE.CONVERSATION') }}</th>
            <th class="px-4 py-3 text-left whitespace-nowrap">{{ t('RESOLUTION_REPORTS.TABLE.CONTACT') }}</th>
            <th class="px-4 py-3 text-left min-w-[200px]">{{ t('RESOLUTION_REPORTS.TABLE.REASON') }}</th>
            <th class="px-4 py-3 text-left whitespace-nowrap">{{ t('RESOLUTION_REPORTS.TABLE.RESOLVED_BY') }}</th>
            <th class="px-4 py-3 text-left whitespace-nowrap">{{ t('RESOLUTION_REPORTS.TABLE.INBOX') }}</th>
            <th class="px-4 py-3 text-center whitespace-nowrap">{{ t('RESOLUTION_REPORTS.TABLE.ACTIONS') }}</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="record in records"
            :key="record.id"
            class="hover:bg-n-alpha-1 transition-colors"
          >
            <!-- Date -->
            <td class="px-4 py-3 whitespace-nowrap text-xs text-n-slate-10">
              {{ formatDate(record.resolved_at) }}
            </td>

            <!-- Conversation -->
            <td class="px-4 py-3 whitespace-nowrap font-medium text-n-slate-12">
              #{{ record.display_id }}
            </td>

            <!-- Contact -->
            <td class="px-4 py-3 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <img
                  v-if="record.contact?.avatar_url"
                  :src="record.contact.avatar_url"
                  class="w-6 h-6 rounded-full object-cover"
                  :alt="record.contact?.name"
                />
                <span
                  v-else
                  class="w-6 h-6 rounded-full bg-n-alpha-3 flex items-center justify-center text-xs font-medium text-n-slate-11"
                >
                  {{ (record.contact?.name || '?').charAt(0).toUpperCase() }}
                </span>
                <span class="truncate max-w-[120px]">{{ record.contact?.name || '—' }}</span>
              </div>
            </td>

            <!-- Reason -->
            <td class="px-4 py-3">
              <template v-if="record.resolution_reason">
                <span
                  :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs border font-medium', reasonBadgeClass(record.resolution_reason)]"
                >
                  {{ reasonCategory(record.resolution_reason) || record.resolution_reason }}
                </span>
                <p v-if="reasonDetail(record.resolution_reason)" class="text-xs text-n-slate-10 mt-1 truncate max-w-[220px]">
                  {{ reasonDetail(record.resolution_reason) }}
                </p>
              </template>
              <span v-else class="text-n-slate-8 italic text-xs">{{ t('RESOLUTION_REPORTS.TABLE.NO_REASON') }}</span>
            </td>

            <!-- Resolved by -->
            <td class="px-4 py-3 whitespace-nowrap">
              <div v-if="record.resolved_by" class="flex items-center gap-2">
                <img
                  v-if="record.resolved_by.avatar_url"
                  :src="record.resolved_by.avatar_url"
                  class="w-6 h-6 rounded-full object-cover"
                  :alt="record.resolved_by.name"
                />
                <span
                  v-else
                  class="w-6 h-6 rounded-full bg-n-brand/20 flex items-center justify-center text-xs font-medium text-n-brand"
                >
                  {{ (record.resolved_by.name || '?').charAt(0).toUpperCase() }}
                </span>
                <span class="truncate max-w-[120px]">{{ record.resolved_by.name }}</span>
              </div>
              <span v-else class="text-n-slate-8 italic text-xs">{{ t('RESOLUTION_REPORTS.TABLE.SYSTEM') }}</span>
            </td>

            <!-- Inbox -->
            <td class="px-4 py-3 whitespace-nowrap text-xs">
              {{ record.inbox?.name || '—' }}
            </td>

            <!-- Actions -->
            <td class="px-4 py-3 text-center">
              <button
                class="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-n-weak text-n-slate-10 hover:bg-n-brand hover:text-white hover:border-n-brand transition-colors"
                :title="t('RESOLUTION_REPORTS.TABLE.OPEN_CONVERSATION')"
                @click="openConversation(record.display_id)"
              >
                <span class="i-lucide-external-link w-3.5 h-3.5" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-2">
      <button
        :disabled="page <= 1"
        class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 disabled:opacity-40 hover:bg-n-alpha-2 transition-colors"
        @click="page--"
      >
        <span class="i-lucide-chevron-left w-4 h-4" />
      </button>
      <span class="text-sm text-n-slate-10">
        {{ page }} / {{ totalPages }}
      </span>
      <button
        :disabled="page >= totalPages"
        class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 disabled:opacity-40 hover:bg-n-alpha-2 transition-colors"
        @click="page++"
      >
        <span class="i-lucide-chevron-right w-4 h-4" />
      </button>
    </div>
  </div>
</template>
